



import { runtimeConfig } from '../config/runtimeConfig';

// Get environment configuration from runtime config
const API_BASE_URL = runtimeConfig.REACT_APP_API_URL;

function buildUserAttributes(userData: Record<string, any>, username: string) {
  const role = userData.admin_flag
    ? 'admin'
    : userData.billing_agent_flag
      ? 'billing_agent'
      : 'provider';
  return {
    email: userData.email || userData.user_email || username,
    'custom:role': role,
    'custom:provider_id': userData.provider_id?.toString() || '',
    'custom:user_id': userData.user_id?.toString() || '',
    'custom:admin_flag': userData.admin_flag?.toString() || 'false',
    'custom:billing_agent_flag': userData.billing_agent_flag?.toString() || 'false',
    'custom:billing_agent_id': userData.billing_agent_id?.toString() || '',
    'custom:accessible_provider_ids': JSON.stringify(userData.accessible_provider_ids || []),
    'custom:is_active': userData.is_active?.toString() || 'false',
    'custom:is_verified': userData.is_verified?.toString() || 'false',
    given_name: userData.first_name || '',
    family_name: userData.last_name || '',
  };
}

interface User {
  username: string;
  attributes: {
    email: string;
    'custom:role': string;
    'custom:provider_id': string;
    'custom:user_id': string;
    'custom:admin_flag': string;
    'custom:billing_agent_flag': string;
    'custom:billing_agent_id': string;
    'custom:accessible_provider_ids': string;
    'custom:is_active': string;
    'custom:is_verified': string;
    given_name: string;
    family_name: string;
  };
  access_token?: string;
  token_expires_at?: string;
}



class AuthService {
  private currentUser: User | null = null;

  async signIn(username: string, password: string): Promise<User> {
    console.log('AuthService.signIn', { username });
    
    try {
      console.log('Using OAuth2 token endpoint for authentication');
      
      // Use OAuth2 token endpoint with form data
      const formData = new URLSearchParams();
      formData.append('username', username);
      formData.append('password', password);
      
      const tokenResponse = await fetch(`${API_BASE_URL}/api/v1/auth/token`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formData
      });
      
      if (!tokenResponse.ok) {
        throw new Error('Invalid credentials');
      }
      
      const tokenData = await tokenResponse.json();
      
      // Now get user information using the token
      const userResponse = await fetch(`${API_BASE_URL}/api/v1/auth/me`, {
        headers: {
          'Authorization': `Bearer ${tokenData.access_token}`,
          'Content-Type': 'application/json',
        }
      });
      
      if (!userResponse.ok) {
        throw new Error('Failed to get user information');
      }
      
      const userData = await userResponse.json();
      
      this.currentUser = {
        username: userData.email || username,
        attributes: buildUserAttributes(userData, username),
        access_token: tokenData.access_token,
        token_expires_at: tokenData.expiration ? new Date(Date.now() + tokenData.expiration * 1000).toISOString() : undefined
      };
      this.userDataCache = {
        data: this.currentUser,
        timestamp: Date.now(),
      };
      
      // Store token in localStorage for persistence
      localStorage.setItem('access_token', tokenData.access_token);
      if (tokenData.expiration && this.currentUser?.token_expires_at) {
        localStorage.setItem('token_expires_at', this.currentUser.token_expires_at);
      }
      
      return this.currentUser;
    } catch (error) {
      console.error('Auth error:', error);
      throw new Error('Invalid credentials');
    }
  }

  async signOut(): Promise<void> {
    console.log('AuthService.signOut');
    this.currentUser = null;
    this.userDataCache = null;
    // Clear stored tokens
    localStorage.removeItem('access_token');
    localStorage.removeItem('token_expires_at');
    // In a real implementation, you might want to call the logout endpoint
  }

  // Cache for user data to prevent repeated API calls
  private userDataCache: { data: User; timestamp: number } | null = null;
  private readonly CACHE_DURATION = 30000; // 30 seconds

  private setCachedUser(user: User): User {
    this.currentUser = user;
    this.userDataCache = {
      data: user,
      timestamp: Date.now(),
    };
    return user;
  }

  async refreshUserProfile(): Promise<User> {
    const storedToken = localStorage.getItem('access_token');
    if (!storedToken) {
      throw new Error('No authenticated user');
    }
    const userResponse = await fetch(`${API_BASE_URL}/api/v1/auth/me`, {
      headers: {
        Authorization: `Bearer ${storedToken}`,
        'Content-Type': 'application/json',
      },
    });
    if (!userResponse.ok) {
      throw new Error('Failed to refresh user profile');
    }
    const userData = await userResponse.json();
    const storedExpiresAt = localStorage.getItem('token_expires_at') || undefined;
    return this.setCachedUser({
      username: userData.email || userData.user_email,
      attributes: buildUserAttributes(userData, userData.email || userData.user_email),
      access_token: storedToken,
      token_expires_at: storedExpiresAt,
    });
  }

  async getCurrentUser(): Promise<User> {
    console.log('AuthService.getCurrentUser');
    
    // Check if we have cached user data that's still valid
    if (this.userDataCache && (Date.now() - this.userDataCache.timestamp) < this.CACHE_DURATION) {
      console.log('Using cached user data');
      // Keep in-memory user in sync so refreshToken always has a token
      this.currentUser = this.userDataCache.data;
      return this.userDataCache.data;
    }
    
    if (!this.currentUser) {
      // Try to restore from localStorage
      const storedToken = localStorage.getItem('access_token');
      const storedExpiresAt = localStorage.getItem('token_expires_at');
      
      if (storedToken && storedExpiresAt) {
        // Check if token is still valid
        const expiresAt = new Date(storedExpiresAt);
        if (expiresAt > new Date()) {
          // Token is still valid, try to get user info
          try {
            const userResponse = await fetch(`${API_BASE_URL}/api/v1/auth/me`, {
              headers: {
                'Authorization': `Bearer ${storedToken}`,
                'Content-Type': 'application/json',
              }
            });
            
            if (userResponse.ok) {
              const userData = await userResponse.json();
              return this.setCachedUser({
                username: userData.email,
                attributes: buildUserAttributes(userData, userData.email),
                access_token: storedToken,
                token_expires_at: storedExpiresAt
              });
            }
          } catch (error) {
            console.error('Failed to restore user session:', error);
          }
        }
      }
      
      // Clear invalid stored data
      this.userDataCache = null;
      localStorage.removeItem('access_token');
      localStorage.removeItem('token_expires_at');
      throw new Error('No authenticated user');
    }
    
    return this.setCachedUser(this.currentUser);
  }

  async refreshToken(): Promise<string> {
    console.log('AuthService.refreshToken');
    const storedToken = localStorage.getItem('access_token');
    const token = this.currentUser?.access_token || storedToken;
    if (!token) {
      throw new Error('No authenticated user');
    }
    
    try {
      const response = await fetch(`${API_BASE_URL}/api/v1/auth/refresh`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to refresh token');
      }
      
      const tokenData = await response.json();
      const tokenExpiresAt = tokenData.expiration
        ? new Date(Date.now() + tokenData.expiration * 1000).toISOString()
        : undefined;
      
      // Update localStorage first so profile restore can use the new token
      localStorage.setItem('access_token', tokenData.access_token);
      if (tokenExpiresAt) {
        localStorage.setItem('token_expires_at', tokenExpiresAt);
      }

      if (this.currentUser) {
        this.currentUser.access_token = tokenData.access_token;
        this.currentUser.token_expires_at = tokenExpiresAt;
        this.setCachedUser(this.currentUser);
      } else {
        // Restore profile if in-memory user was missing (cache/localStorage only)
        try {
          await this.refreshUserProfile();
        } catch (profileError) {
          console.warn('Token refreshed but profile restore failed:', profileError);
          this.setCachedUser({
            username: tokenData.user_email || 'user',
            attributes: buildUserAttributes(tokenData, tokenData.user_email || 'user'),
            access_token: tokenData.access_token,
            token_expires_at: tokenExpiresAt,
          });
        }
      }
      
      return tokenData.access_token;
    } catch (error) {
      console.error('Token refresh failed:', error);
      // Clear invalid session
      this.currentUser = null;
      this.userDataCache = null;
      localStorage.removeItem('access_token');
      localStorage.removeItem('token_expires_at');
      throw error;
    }
  }
}

export const authService = new AuthService();

export async function requestPasswordReset(email: string): Promise<string> {
  const response = await fetch(`${API_BASE_URL}/api/v1/auth/forgot-password`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ user_email: email }),
  });
  const data = await response.json();
  if (!response.ok) {
    const detail = data.detail;
    throw new Error(typeof detail === 'string' ? detail : 'Unable to process request');
  }
  return data.message;
}

export async function confirmPasswordReset(token: string, newPassword: string): Promise<string> {
  const response = await fetch(`${API_BASE_URL}/api/v1/auth/reset-password`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ token, new_password: newPassword }),
  });
  const data = await response.json();
  if (!response.ok) {
    const detail = data.detail;
    throw new Error(typeof detail === 'string' ? detail : 'Unable to reset password');
  }
  return data.message;
}